import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, groupBy, uniqWith, orderBy } from 'lodash-es';
import { BuildPriceEntities } from 'client/data/models/build-price';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { COLOR_CATEGORY_NAME, COLOR_SECTION_RENDER_MAP } from 'site-modules/shared/constants/build-price';
import { ColorListSmart } from 'site-modules/shared/components/color-list-smart/color-list-smart';

export function ColorSection({ features, optionIds, hasColorPhoto = false, onOptionUpdate }) {
  const onColorClick = useCallback(
    colorName => {
      const optionId = get(
        features.find(({ oemName }) => colorName === oemName),
        'id'
      );

      if (!optionId || optionIds.includes(optionId)) {
        return;
      }

      onOptionUpdate(optionId);
    },
    [features, onOptionUpdate, optionIds]
  );

  const colorOptions = uniqWith(
    orderBy(
      features
        .filter(({ primaryRGB }) => primaryRGB)
        .map(({ id, oemName, price, primaryRGB, colorType }) => ({
          id,
          category: colorType,
          price,
          name: oemName,
          properties: {
            rgb: primaryRGB.toString(),
          },
          trackingId: 'build_price_select_color',
          trackingValue: `${COLOR_CATEGORY_NAME[colorType]}: ${oemName}`,
        })),
      ['price.baseMSRP', 'name']
    ),
    (colorA, colorB) => colorA.category === colorB.category && colorA.name === colorB.name
  );
  const groupedColors = groupBy(colorOptions, 'category');

  return (
    <div className="color-section text-gray-darker ps-3">
      {COLOR_SECTION_RENDER_MAP.map(({ path, title, squared }) => {
        const colors = get(groupedColors, path, []);
        if (!colors.length) {
          return null;
        }

        const selectedColor = colors.find(({ id }) => optionIds.includes(id));

        return (
          <fieldset key={`color-section-${title}`} className="mb-0_75">
            <legend className="size-16 mb-1" aria-label={`Choose ${title} color`}>
              <span className="fw-bold">{title}: </span>
              {!!selectedColor && `${selectedColor.name} ${formatPriceString(get(selectedColor, 'price.baseMSRP', 0))}`}
            </legend>
            <ColorListSmart
              keyPrefix={title}
              inputType="radio"
              listItems={colors}
              className="px-0_25 text-center"
              itemClassName={classnames('mb-1_5', { squared })}
              showCheckmark={false}
              selectedItems={selectedColor ? { [selectedColor.name]: true } : {}}
              updateHOCState={onColorClick}
              showPrice
            />
            {title === COLOR_CATEGORY_NAME.EXTERIOR && !hasColorPhoto && (
              <div className="small mb-1_5">Sorry, no vehicle image available for this color</div>
            )}
          </fieldset>
        );
      })}
    </div>
  );
}

ColorSection.propTypes = {
  features: PropTypes.arrayOf(BuildPriceEntities.Option).isRequired,
  optionIds: PropTypes.string.isRequired,
  onOptionUpdate: PropTypes.func.isRequired,
  hasColorPhoto: PropTypes.bool,
};
